import GenericLoading from '@cannect/components/GenericLoading'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import { useToggleProductFavorite } from '@cannect/services/resources/products'
import iconFlag from 'assets/img/aspaec/icon-flag.png'
import iconSend from 'assets/img/aspaec/icon-send.png'
import logoAspaec from 'assets/img/aspaec/logotipo_topo.png'
import { useAuth } from 'hooks/AuthContext'
import { getPathFrom, removePahtFrom, setPathFrom } from 'hooks/usePathsRedirect'
import { usePendingActions } from 'hooks/usePendingActions'
import { useTokenApi } from 'hooks/useTokenApi'
import { FormEvent, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { toast } from 'react-toastify'
import { setUser } from '../../hooks/useSessionUser'
import api, { defaultsHeadersAxios } from '../../services/api'
import { clearSessionCartId, getSessionCartId, markSessionCartResolved } from '../../utils/anonymousCart'
import {
  CheckboxContainer,
  ContactBox,
  ContactItem,
  ForgotPasswordLink,
  HeaderAspaec,
  InputGroup,
  LoginBox,
  LoginButton,
  LoginContainer,
  LoginForm,
  MainContent,
  RegisterLink
} from './styles'

export default function LoginAspaec() {
  const { push } = useHistory()
  const { state } = useLocation() as any
  const { getRoles } = useAuth()
  const { setTokenApi } = useTokenApi()
  const { getPendingAction, removePendingAction } = usePendingActions()
  const { addToCart, setOrderId } = usePrepareCart()
  const toggleFavoriteMutation = useToggleProductFavorite()
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [remind, setRemind] = useState(false)

  // Carrega email salvo se "lembrar e-mail" estava marcado
  useEffect(() => {
    const savedEmail = localStorage.getItem('aspaec_remember_email')
    if (savedEmail) {
      setEmail(savedEmail)
      setRemind(true)
    }
  }, [])

  const handleSendCode = async (event: FormEvent) => {
    event.preventDefault()
    try {
      setLoading(true)

      // Salva email no localStorage se marcado para lembrar
      if (remind) {
        localStorage.setItem('aspaec_remember_email', email)
      } else {
        localStorage.removeItem('aspaec_remember_email')
      }

      // Envia código por email
      await api.post('/send-otp', { email })
      toast.success('Código enviado para seu e-mail!')
      setStep('code')
      setCode('') // Limpa o campo de código
    } catch (e: any) {
      toast.error('Erro ao enviar código. Verifique seu e-mail.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async (event: FormEvent) => {
    event.preventDefault()
    try {
      setLoading(true)
      // Valida o código e faz login
      const sessionCartId = getSessionCartId()
      const response = await api.post('/verify-otp', {
        email,
        code,
        ...(sessionCartId ? { session_cart_id: sessionCartId } : {})
      })
      if (sessionCartId) {
        markSessionCartResolved(sessionCartId)
      }

      setTokenApi(response.data.token)
      defaultsHeadersAxios(response.data.token)
      await setUser(response.data.user)
      getRoles()

      const pendingAction = getPendingAction()
      if (pendingAction) {
        setTimeout(() => {
          try {
            switch (pendingAction.type) {
              case 'ADD_TO_CART':
                const { productId, quantity, classId, brandId } = pendingAction.payload
                if (productId && quantity && classId && brandId) {
                  addToCart(productId, quantity, classId, brandId)
                  toast.success('Produto adicionado ao carrinho!')
                }
                break
              case 'TOGGLE_FAVORITE':
                if (pendingAction.payload.productId) {
                  toggleFavoriteMutation.mutate(pendingAction.payload.productId)
                }
                break
              default:
                break
            }
          } catch (error) {
            console.error('Erro ao executar ação pendente:', error)
          }
          removePendingAction()
        }, 1000)
      }

      if (response.data.order_id) {
        setOrderId(response.data.order_id)
        clearSessionCartId()
        push('/carrinho')
      } else {
        const pathFrom = await getPathFrom()
        const pageToRedirect = pathFrom || '/AreaAssociados/MinhaConta'
        push(pageToRedirect)
      }

      await removePahtFrom()
      toast.success('Login realizado com sucesso!')
    } catch (e: any) {
      toast.error('Código inválido ou expirado')
    } finally {
      setLoading(false)
    }
  }

  const handleToRegister = () => {
    push({ pathname: `/cadastro-associado`, state: { from: { pathname: state?.from?.pathname } } })
  }

  useEffect(() => {
    if (state && state?.from && state?.from?.pathname) {
      setPathFrom(state?.from?.search ? `${state?.from?.pathname}${state?.from?.search}` : state?.from?.pathname)
    }
  }, [])

  return (
    <LoginContainer>
      <GenericLoading loading={loading} />

      {/* Header */}
      <HeaderAspaec>
        <img src={logoAspaec} alt="ASPAEC" />
      </HeaderAspaec>

      {/* Main Content */}
      <MainContent>
        {/* Login Box */}
        <LoginBox>
          <h2>{step === 'email' ? 'Faça login em sua conta' : 'Digite o código enviado'}</h2>

          {step === 'email' ? (
            <LoginForm onSubmit={handleSendCode}>
              <InputGroup>
                <input
                  type="email"
                  placeholder="Digite seu e-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </InputGroup>

              <CheckboxContainer>
                <label>
                  <input type="checkbox" checked={remind} onChange={(e) => setRemind(e.target.checked)} />
                  <span>Lembrar e-mail</span>
                </label>
              </CheckboxContainer>

              <LoginButton type="submit">ENVIAR CÓDIGO</LoginButton>

              <RegisterLink>
                <button type="button" onClick={handleToRegister}>
                  Cadastre-se
                </button>
              </RegisterLink>
            </LoginForm>
          ) : (
            <LoginForm onSubmit={handleVerifyCode}>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                Enviamos um código de verificação para <strong>{email}</strong>
              </p>

              <InputGroup>
                <input
                  type="text"
                  placeholder="Código de 6 dígitos"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={6}
                  required
                />
              </InputGroup>

              <ForgotPasswordLink type="button" onClick={() => setStep('email')}>
                ← Voltar para o e-mail
              </ForgotPasswordLink>

              <LoginButton type="submit">ENTRAR</LoginButton>

              <RegisterLink>
                <button type="button" onClick={handleSendCode}>
                  Reenviar código
                </button>
              </RegisterLink>
            </LoginForm>
          )}
        </LoginBox>

        {/* Contact Box */}
        <ContactBox>
          <h3>Entre em Contato</h3>
          <p>Utilize um de nossos canais abaixo para entrar em contato conosco.</p>

          <ContactItem>
            <img src={iconSend} alt="Email" />
            <div>
              <a href="mailto:contato.aspaec@gmail.com">contato.aspaec@gmail.com</a>
            </div>
          </ContactItem>

          <ContactItem>
            <img src={iconFlag} alt="Endereço" />
            <div>
              <strong>Avenida Moxoto, 360, Oliveira Brito</strong>
              <p>Paulo Afonso - BA</p>
            </div>
          </ContactItem>
        </ContactBox>
      </MainContent>

      {/* Footer */}
      {/* <Footer>
        <a href={politicaPdf} target="_blank" rel="noopener noreferrer">
          Política de Privacidade
        </a>
      </Footer> */}
    </LoginContainer>
  )
}
